import { Alert, Box, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CoursesApi from '../../../apis/CoursesApi';
import ManagementApi from '../../../apis/ManagementApi';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { MembersGridLineDto } from '../../../generated-types/membersGridLineDto';
import useApi from '../../../lib/useApi';
import { renderWithOverflow } from '../../GridItemRenderOverflow/GridItemRenderOverflow';
import DataRefreshContext from '../lib/DataRefreshContext';
import ManagementGridProps from '../lib/ManagementGridProps';
import InviteMembersDialog from './InviteMembersDialog';
import MembersGridToolbar from './MembersGridToolbar';
import RemoveMembersDialog from './RemoveMembersDialog';

/**
 * The content component for the members grid card.
 * Manages the display and interaction logic for the members data grid.
 */
const MembersGridContent = (props: ManagementGridProps) => {
  // State variables
  const [showInviteMembersDialog, setShowInviteMembersDialog] = useState(false);
  const [courseHeaders, setCourseHeaders] = useState<Record<string, string>>(
    {}
  );
  const [rows, setRows] = useState<MembersGridLineDto[]>([]);
  const [processedRows, setProcessedRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showRemoveMembersDialog, setShowRemoveMembersDialog] = useState(false);
  const [selectedExpectedMembers, setSelectedExpectedMembers] = useState<any[]>(
    []
  );
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState(false);
  const managementApi = useApi(ManagementApi);
  const coursesApi = useApi(CoursesApi);
  const snackbar = useContext(SnackbarContext);

  // Access the triggerDataRefresh function from the context
  const { triggerDataRefresh } = useContext(DataRefreshContext);

  /**
   * Refresh the members list
   */
  const refreshMembersList = useCallback(async () => {
    setError(false);
    const getMembersPromise = props.orgId
      ? managementApi.GetMembersWithOrgId(props.orgId)
      : managementApi.GetMembers();
    return getMembersPromise.then(setRows).catch(() => setError(true));
  }, [managementApi, props.orgId]);

  /**
   * Fetches the course ID-title mapping when the component mounts.
   */
  useEffect(() => {
    coursesApi
      .GetIdNameMapping()
      .then(setCourseHeaders)
      .catch(() => setIsLoading(false));
  }, [coursesApi]);

  /**
   * Fetches the members data once the course headers have been loaded.
   */
  useEffect(() => {
    // Load the rows
    refreshMembersList().finally(() => setIsLoading(false));
  }, [courseHeaders, managementApi, refreshMembersList]);

  /**
   * Processes the raw member data to include necessary fields for the data grid.
   */
  useEffect(() => {
    // If no rows exist, return early
    if (rows.length === 0) return;

    // Preprocess the rows
    setProcessedRows(
      rows.map((member) => {
        const processedMember = {
          ...member,
          lastLogin: member.lastLogin ? new Date(member.lastLogin) : null,
        } as any;

        // Add course progress fields based on mapping
        member.courseProgress.forEach((progress) => {
          if (courseHeaders[progress.courseId]) {
            processedMember[progress.courseId] = progress.progressPercent * 100;
          }
        });

        return processedMember;
      })
    );
  }, [courseHeaders, rows]);

  // Set the selected rows data
  const selectedRowsData = useMemo(() => {
    return processedRows.filter((row) => selectedRows.includes(row.id));
  }, [processedRows, selectedRows]);

  // Determine if the remove button should be disabled
  const disableRemoveButton = useMemo(() => {
    return !selectedRowsData.some((row) => row.isExpected);
  }, [selectedRowsData]);

  /**
   * Handles the Remove Members button click event.
   * Filters out non-expected members and updates the selection.
   */
  const handleRemoveMembersClick = useCallback(() => {
    // Filter selected expected members
    const expectedMembers = selectedRowsData.filter((row) => row.isExpected);

    // Check for non-expected members
    if (expectedMembers.length < selectedRowsData.length) {
      snackbar.setAlert(
        <Alert severity="warning" variant="filled">
          Only expected members can be removed. The others have been unselected.
        </Alert>
      );
      snackbar.triggerSnackbar();

      // Unselect non-expected members
      const expectedMemberIds = expectedMembers.map((member) => member.id);
      setSelectedRows(expectedMemberIds);
    }

    // Set the expected members to state and show the dialog
    setSelectedExpectedMembers(expectedMembers);
    setShowRemoveMembersDialog(true);
  }, [selectedRowsData, snackbar]);

  /**
   * Handles the confirmation of removing expected members.
   */
  const handleConfirmRemoveMembers = useCallback(async () => {
    setIsRemoving(true);
    try {
      // Get the IDs of the expected members to remove
      const memberIds = selectedExpectedMembers.map((member) => member.id);

      // Call the API to remove expected members
      await managementApi.RemoveExpectedMembers(memberIds);

      // Refresh the members list
      await refreshMembersList();

      // Trigger data refresh for other components
      triggerDataRefresh();

      // Close the dialog
      setShowRemoveMembersDialog(false);

      // Clear the selection
      setSelectedRows([]);
      // Show success message
      snackbar.setAlert(
        <Alert severity="success" variant="filled">
          Members removed successfully.
        </Alert>
      );
      snackbar.triggerSnackbar();
    } catch (error) {
      console.error('Failed to remove members:', error);
      // Show error message
      snackbar.setAlert(
        <Alert severity="error" variant="filled">
          An error occurred while removing members. Please try again.
        </Alert>
      );
      snackbar.triggerSnackbar();
    } finally {
      setIsRemoving(false);
    }
  }, [
    selectedExpectedMembers,
    managementApi,
    refreshMembersList,
    triggerDataRefresh,
    snackbar,
  ]);

  // Define base columns
  const baseColumns: GridColDef[] = [
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 1,
      renderCell: renderWithOverflow,
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1,
      renderCell: renderWithOverflow,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      renderCell: renderWithOverflow,
    },
    {
      field: 'lastLogin',
      headerName: 'Last Login',
      flex: 1,
      type: 'dateTime',
      /**
       * Custom value formatter for the Last Login column.
       * Displays 'Expected Member' if the member is expected, otherwise formats the date.
       */
      valueFormatter: (params) => {
        const row = params.api.getRow(params.id!);
        return row.isExpected
          ? 'Expected Member'
          : params.value
            ? (params.value as Date).toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : '';
      },
    },
  ];

  // Define dynamic columns for course progress based on mapping
  const courseColumns: GridColDef[] = Object.keys(courseHeaders).map(
    (courseId) => ({
      field: courseId,
      headerName: courseHeaders[courseId],
      flex: 1,
      type: 'number',
      /**
       * Retrieves the course progress value for the given course ID.
       */
      valueGetter: (params) => params.row[courseId] || 0, // Display 0 if no progress is available
      /**
       * Formats the course progress value as a percentage.
       */
      valueFormatter: (params) => `${Math.round(params.value)}%`, // Display as percentage
    })
  );

  // Combine base and dynamic columns
  const columns: GridColDef[] = [...baseColumns, ...courseColumns];

  // Estimate a comfortable minimum width for the grid
  const calculatedMinWidth = 200 * (baseColumns.length + courseColumns.length);

  return (
    <>
      {/* Container for the data grid */}
      <Box
        position="relative"
        sx={{ height: '100%', width: '100%', overflowX: 'scroll' }}
      >
        {error ? (
          <Box
            padding={2}
            minHeight="8em"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography textAlign="center">
              An error occurred while loading the members grid. Please refresh
              your page to try again. If this continues, contact us for
              assistance!
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={processedRows}
            columns={columns}
            loading={isLoading}
            autoHeight
            checkboxSelection
            sx={{
              minWidth: calculatedMinWidth,
            }}
            /**
             * Handles changes to the row selection.
             * Updates the selectedRows state with the new selection.
             */
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection as string[]);
            }}
            /**
             * Controls the selection model to ensure the grid's selection reflects the state.
             */
            rowSelectionModel={selectedRows}
            /**
             * Customizes the data grid toolbar and passes necessary props.
             */
            slots={{
              toolbar: MembersGridToolbar,
            }}
            slotProps={{
              toolbar: {
                onInviteClick: () => setShowInviteMembersDialog(true),
                onRemoveClick: handleRemoveMembersClick,
                disableRemoveButton: disableRemoveButton,
                managementGridProps: props,
              },
            }}
          />
        )}
      </Box>
      {/* Dialog for inviting members */}
      <InviteMembersDialog
        showDialog={showInviteMembersDialog}
        setShowDialog={setShowInviteMembersDialog}
        reloadGrid={() => {
          setIsLoading(true);
          refreshMembersList().finally(() => setIsLoading(false));
        }}
      />
      {/* Dialog for confirming removal of members */}
      <RemoveMembersDialog
        open={showRemoveMembersDialog}
        onClose={() => setShowRemoveMembersDialog(false)}
        members={selectedExpectedMembers}
        isLoading={isRemoving}
        onConfirm={handleConfirmRemoveMembers}
      />
    </>
  );
};

export default MembersGridContent;
